import {
  PROMPTS_CHECKPOINT_SIZE,
  normalizeEditImagePrompts,
} from "../components/Modal/ModalImageEdit/hooks/imageEditCheckpoint";

const resolveTurnResultUrl = ({ turn, isLastTurn, isSuccess, responseUrl }) => {
  if (turn.resultUrl) {
    return turn.resultUrl;
  }

  if (isLastTurn && isSuccess && responseUrl) {
    return responseUrl;
  }

  return null;
};

/**
 * Builds ModalImageEdit session state from an editImage RTDB job snapshot.
 */
export const buildImageEditSessionFromJob = ({
  payloadSnapshot = {},
  response,
  getEditedImageLabel = (version) => `Edited Image v${version}`,
  processingMessage = "Thinking",
  isSuccess = true,
  isInProgress = false,
  errorMessage = null,
} = {}) => {
  const originalUrl = payloadSnapshot.url || null;
  const responseUrl = response?.data?.url || null;
  const turns = normalizeEditImagePrompts(
    payloadSnapshot.prompts || payloadSnapshot.conversation || []
  );

  const chatMessages = [];
  let previousImageUrl = originalUrl;
  let version = 0;

  turns.forEach((turn, index) => {
    const isLastTurn = index === turns.length - 1;

    chatMessages.push({
      id: `edit-alert-user-${index}`,
      role: "user",
      content: turn.prompt,
      timestamp: new Date(),
      imageUrl: previousImageUrl,
      attachmentUrls: turn.attachmentUrls,
      attachmentFileNames: {},
    });

    const turnResultUrl = resolveTurnResultUrl({
      turn,
      isLastTurn,
      isSuccess,
      responseUrl,
    });

    if (turnResultUrl) {
      version += 1;
      chatMessages.push({
        id: `edit-alert-assistant-${index}`,
        role: "assistant",
        content: getEditedImageLabel(version),
        timestamp: new Date(),
        version,
        imageUrl: turnResultUrl,
      });
      previousImageUrl = turnResultUrl;
      return;
    }

    if (!isLastTurn) {
      return;
    }

    if (isInProgress) {
      chatMessages.push({
        id: `edit-alert-thinking-${index}`,
        role: "assistant",
        content: processingMessage,
        timestamp: new Date(),
        isThinking: true,
      });
      return;
    }

    if (errorMessage) {
      chatMessages.push({
        id: `edit-alert-error-${index}`,
        role: "assistant",
        content: errorMessage,
        timestamp: new Date(),
        isError: true,
      });
    }
  });

  const turnsWithResults = turns.map((turn, index) => {
    const resultUrl = resolveTurnResultUrl({
      turn,
      isLastTurn: index === turns.length - 1,
      isSuccess,
      responseUrl,
    });

    return resultUrl ? { ...turn, resultUrl } : turn;
  });

  const completedTurns = turnsWithResults
    .filter((turn) => turn.resultUrl)
    .map(({ prompt, attachmentUrls, resultUrl }) => ({
      prompt,
      attachmentUrls,
      resultUrl,
    }));

  const finalUrl =
    completedTurns.length > 0
      ? completedTurns[completedTurns.length - 1].resultUrl
      : isSuccess
        ? responseUrl
        : null;

  const displayImageUrl = finalUrl || originalUrl;
  const hasCompletedCheckpointBlock =
    completedTurns.length >= PROMPTS_CHECKPOINT_SIZE && Boolean(finalUrl);

  const blockBaseImageUrl = hasCompletedCheckpointBlock
    ? finalUrl
    : originalUrl;
  const checkpointPromptsHistory = hasCompletedCheckpointBlock
    ? []
    : completedTurns;

  return {
    url: displayImageUrl || originalUrl,
    initialImageUrl: originalUrl,
    initialResultImageUrl: finalUrl,
    initialConversation: turns.map(({ prompt, attachmentUrls }) => ({
      prompt,
      attachmentUrls,
    })),
    initialChatMessages: chatMessages,
    initialAttachmentUrls: [],
    blockBaseImageUrl,
    checkpointPromptsHistory,
    fromAlert: true,
    isJobInProgress: isInProgress,
  };
};
